import type { GameState, AIDifficulty } from '@/types';
import type { GameEngine } from '@core/GameEngine';
import { AIPlayer, type AITurn, type AIThinkingProgress } from './AIPlayer';

export type { AIThinkingProgress };

/**
 * Timing constants for AI turn execution
 */
const AI_TIMING = {
  THINKING_DELAY: 800, // Consistent delay before AI starts thinking
  WARRIOR_SELECT_DELAY: 400, // Delay after selecting warrior
  ACTION_EXECUTE_DELAY: 300, // Delay before executing action
};

export interface AIControllerCallbacks {
  onThinkingStart?: () => void;
  onThinkingEnd?: () => void;
  onThinkingProgress?: (progress: AIThinkingProgress) => void;
  onWarriorSelected?: (warriorId: string) => void;
  onActionExecuted?: () => void;
}

/**
 * Manages AI turn execution with timing and callbacks
 */
export class AIController {
  private aiPlayer: AIPlayer;
  private engine: GameEngine;
  private difficulty: AIDifficulty;
  private isProcessing: boolean = false;
  private callbacks: AIControllerCallbacks;

  constructor(
    engine: GameEngine,
    difficulty: AIDifficulty,
    callbacks: AIControllerCallbacks = {}
  ) {
    this.engine = engine;
    this.difficulty = difficulty;
    this.aiPlayer = new AIPlayer(difficulty);
    this.callbacks = callbacks;
  }

  /**
   * Check if the AI should take a turn and execute it
   */
  async checkAndExecuteTurn(): Promise<void> {
    const state = this.engine.getState();

    if (!this.shouldExecute(state)) {
      return;
    }

    await this.executeAITurn(state);
  }

  /**
   * Check if AI should execute (it's AI's turn in battle phase)
   */
  private shouldExecute(state: GameState): boolean {
    return (
      state.gameMode === '1-player' &&
      state.currentTurn === 'player2' &&
      state.phase === 'battle' &&
      !this.isProcessing &&
      state.winner === null
    );
  }

  /**
   * Execute the AI turn with appropriate delays
   */
  private async executeAITurn(state: GameState): Promise<void> {
    this.isProcessing = true;
    let thinkingIndicatorShown = false;

    try {
      // Wait for thinking delay
      await this.delay(AI_TIMING.THINKING_DELAY);

      // Start a timer to show thinking indicator after 500ms
      const thinkingTimeout = setTimeout(() => {
        thinkingIndicatorShown = true;
        this.callbacks.onThinkingStart?.();
      }, 500);

      // Compute the best turn with progress callback (only if indicator is shown)
      const progressCallback = this.callbacks.onThinkingProgress
        ? (progress: AIThinkingProgress) => {
            if (thinkingIndicatorShown) {
              this.callbacks.onThinkingProgress?.(progress);
            }
          }
        : undefined;

      const turn = await this.aiPlayer.computeTurn(state, progressCallback);

      // Clear the timeout if thinking finished before 500ms
      clearTimeout(thinkingTimeout);

      // Notify thinking is done (only if we showed the indicator)
      if (thinkingIndicatorShown) {
        this.callbacks.onThinkingEnd?.();
      }

      // Execute the turn
      await this.executeTurn(turn);
    } catch (error) {
      console.error('AI turn execution failed:', error);
      if (thinkingIndicatorShown) {
        this.callbacks.onThinkingEnd?.();
      }
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Execute the computed turn
   */
  private async executeTurn(turn: AITurn): Promise<void> {
    // Select the warrior
    this.engine.selectWarrior(turn.warriorId);
    this.callbacks.onWarriorSelected?.(turn.warriorId);

    // Wait a bit after selection
    await this.delay(AI_TIMING.WARRIOR_SELECT_DELAY);

    // Execute the action
    await this.delay(AI_TIMING.ACTION_EXECUTE_DELAY);

    switch (turn.action) {
      case 'move':
        if (turn.position) {
          this.engine.move(turn.position);
        }
        break;
      case 'dash':
        if (turn.position) {
          this.engine.dash(turn.position);
        }
        break;
      case 'attack':
        this.engine.attack();
        break;
      case 'heal':
        this.engine.heal();
        break;
    }

    this.callbacks.onActionExecuted?.();
  }

  /**
   * Helper to delay execution
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if AI is currently processing a turn
   */
  isThinking(): boolean {
    return this.isProcessing;
  }

  /**
   * Get the current difficulty
   */
  getDifficulty(): AIDifficulty {
    return this.difficulty;
  }

  /**
   * Reset AI state (call when starting a new game)
   */
  resetState(): void {
    this.aiPlayer.resetState();
    this.isProcessing = false;
  }
}
